<script setup lang="ts">
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue'
import { ref } from 'vue'
import { useToast } from 'vue-toastification'
import useRole from '@/modules/roles/composables/useRole'
import useHasPermissionUser from '@/composables/useHasPermissionUser'
import CompanyRoleAbilitiesPanel from '@/modules/companies/components/CompanyRoleAbilitiesPanel.vue'
import LogTab from '@/modules/roles/pages/partials/edit/LogTab.vue'
import GeneralTab from '../pages/partials/edit/GeneralTab.vue'
import MembersTab from '../pages/partials/edit/MembersTab.vue'

interface Props {
  roleId: number
  open: boolean
}

interface Emits {
  (e: 'close'): void
}

const props = defineProps<Props>()
const emits = defineEmits<Emits>()

const toast = useToast()
const { hasPermissionUser } = useHasPermissionUser()

const { role, isLoading, updateRoleMutationAsync } = useRole({
  id: props.roleId,
  immediate: true,
})

const handleSave = async (name: string) => {
  if (role.value?.name === name) { toast.info('Sin cambios'); return }
  if (!name.length) { toast.error('Nombre requerido'); return }
  await updateRoleMutationAsync({ name })
}

const TAB_DEFS = [
  { name: 'General',   icon: 'tune',    ability: 'update-internal',       entityType: 'roles' },
  { name: 'Permisos',  icon: 'lock',    ability: 'viewAny-internal',      entityType: 'App\\Models\\Ability' },
  { name: 'Miembros',  icon: 'group',   ability: 'assign-roles-internal', entityType: 'App\\Models\\User' },
  { name: 'Historial', icon: 'history', ability: 'viewAny-internal',      entityType: 'roles' },
]

const visibleTabs = ref(TAB_DEFS.filter((t) => hasPermissionUser(t.ability, t.entityType)))
</script>

<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-50" @close="emits('close')">
      <TransitionChild
        as="template"
        enter="ease-in-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in-out duration-300" leave-from="opacity-100" leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-900/40 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-300" enter-from="translate-x-full" enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-300" leave-from="translate-x-0" leave-to="translate-x-full"
            >
              <DialogPanel class="pointer-events-auto w-screen max-w-2xl">
                <div class="flex h-full flex-col bg-gray-50 shadow-2xl">

                  <div v-if="isLoading" class="flex-1 space-y-4 p-6 animate-pulse">
                    <div class="h-20 rounded-xl bg-gray-200" />
                    <div class="h-10 rounded-lg bg-gray-100" />
                    <div class="h-64 rounded-xl bg-gray-100" />
                  </div>

                  <template v-else-if="role">
                    <div class="flex-shrink-0 bg-white border-b border-gray-200">
                      <div class="relative h-16 bg-gradient-to-br from-[#111d3a] via-[#253C70] to-[#3d6199]">
                        <button
                          type="button"
                          class="absolute top-3 right-4 w-7 h-7 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
                          @click="emits('close')"
                        >
                          <span class="material-symbols-outlined text-[16px]">close</span>
                        </button>
                      </div>

                      <div class="flex items-end gap-4 -mt-6 relative z-10 px-5 pb-3">
                        <div class="w-12 h-12 rounded-xl bg-white shadow border border-gray-100 flex items-center justify-center flex-shrink-0">
                          <span class="material-symbols-outlined text-[22px] text-[#021F58]">shield_person</span>
                        </div>
                        <div class="pb-0.5 min-w-0">
                          <h2 class="text-base font-bold text-gray-900 truncate">{{ role.name }}</h2>
                          <p class="text-xs text-gray-400">Rol interno · {{ role.usersCount }} miembro{{ role.usersCount !== 1 ? 's' : '' }}</p>
                        </div>
                      </div>

                      <TabGroup as="div">
                        <TabList as="div" class="flex items-center gap-1 px-4 pt-1 pb-2">
                          <Tab
                            v-for="tab in visibleTabs"
                            :key="tab.name"
                            v-slot="{ selected }"
                            as="template"
                          >
                            <button
                              :class="[
                                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 focus:outline-none',
                                selected
                                  ? 'bg-[#253C70] text-white shadow-sm'
                                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
                              ]"
                            >
                              <span class="material-symbols-outlined text-[14px]">{{ tab.icon }}</span>
                              <span class="hidden sm:inline">{{ tab.name }}</span>
                            </button>
                          </Tab>
                        </TabList>

                        <div class="overflow-y-auto px-5 py-4" style="max-height: calc(100vh - 180px)">
                          <TabPanels>
                            <template v-for="tab in visibleTabs" :key="tab.name">
                              <TabPanel v-if="tab.name === 'General'">
                                <GeneralTab :role="role" @handle-change-name="handleSave" />
                              </TabPanel>
                              <TabPanel v-else-if="tab.name === 'Permisos'">
                                <CompanyRoleAbilitiesPanel :role="role" />
                              </TabPanel>
                              <TabPanel v-else-if="tab.name === 'Miembros'">
                                <MembersTab :role="role" />
                              </TabPanel>
                              <TabPanel v-else-if="tab.name === 'Historial'">
                                <LogTab :role="role" />
                              </TabPanel>
                            </template>
                          </TabPanels>
                        </div>
                      </TabGroup>
                    </div>
                  </template>

                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
